import {
  HomeIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  UserIcon,
} from "lucide-react";
import { Button } from "./button";
import { useNavigate, useLocation } from "react-router";
import { useCart } from "@nusrif/contexts";

export const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { state } = useCart();
  
  const itemCount = state.cart ? state.cart.lines.reduce((sum, line) => sum + line.quantity, 0) : 0;
  
  const handleHomeClick = () => {
    navigate("/");
  };
  const handleCartClick = () => {
    navigate("/cart");
  };
  const handleNotificationClick = () => {
    navigate("/store");
  };
  const handleProfileClick = () => {
    navigate("/profile");
  };
  // Active state logic for all buttons
  const isHomeActive = pathname === "/";
  const isCartActive = pathname === "/cart";
  const isShoppingStore = pathname === "/store";
  const isProfileActive = pathname === "/profile";
  // Reusable active button component
  const ActiveButton = ({
    icon: Icon,
    label,
    onClick,
  }: {
    icon: any; // Using any to avoid React type conflicts
    label: string;
    onClick: () => void;
  }) => (
    <div
      className="relative w-[86px] h-[40px] bg-gray-200 rounded-[30px] flex items-center cursor-pointer"
      onClick={onClick}
      data-testid={`bottom-navigation-${label?.toLowerCase() || 'button'}`}  
      aria-label={label}
    >
      <div className="absolute w-[40px] h-[40px] top-0 left-0 bg-primary-blue rounded-[20px] flex items-center justify-center">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <span className="absolute left-[44px] font-semibold text-primary-blue text-[11px]">
        {label}
      </span>
    </div>
  );
  // Reusable inactive button component
  const InactiveButton = ({
    icon: Icon,
    onClick,
    className = "w-4 h-4",
    showBadge = false,
    badgeCount = 0,
    label,
  }: {
    icon: any; // Using any to avoid React type conflicts
    onClick: () => void;
    className?: string;
    showBadge?: boolean;
    badgeCount?: number;
    label?: string;
  }) => (
    <Button
      variant="ghost"
      className="p-3 h-auto relative min-h-[44px] min-w-[44px] flex items-center justify-center"
      onClick={onClick}
      data-testid={`bottom-navigation-${label?.toLowerCase() || 'button'}`}  
      aria-label={label}
    >
      <Icon className={`${className} w-6 h-6 text-black`} />
      {showBadge && badgeCount > 0 && (
        <div className="absolute -top-2 -right-2 w-4 h-4 bg-primary-blue rounded-full flex items-center justify-center">
          <span className="text-xs font-bold text-white">
            {badgeCount > 9 ? "9+" : badgeCount}
          </span>
        </div>
      )}
    </Button>
  );
  return (
    <div className="fixed rounded-tl-[1.875rem] rounded-tr-[1.875rem] bottom-0 left-0 right-0 w-full h-[70px] bg-white shadow-[0px_-2px_7px_#0000001a] flex items-center justify-around z-50">
      {/* Home Button */}
      {isHomeActive ? (
        <ActiveButton icon={HomeIcon} label="Home" onClick={handleHomeClick} />
      ) : (
        <InactiveButton icon={HomeIcon} onClick={handleHomeClick} label="Home" />
      )}
      {/* Shopping Button */}
      {isShoppingStore ? (
        <ActiveButton
          icon={ShoppingBagIcon}
          label="Store"
          onClick={handleNotificationClick}
        />
      ) : (
        <InactiveButton
          icon={ShoppingBagIcon}
          onClick={handleNotificationClick}
          label="Store"
        />
      )}
      {/* Cart Button */}
      {isCartActive ? (
        <ActiveButton
          icon={ShoppingCartIcon}
          label="Cart"
          onClick={handleCartClick}
        />
      ) : (
        <InactiveButton
          icon={ShoppingCartIcon}
          onClick={handleCartClick}
          showBadge={true}
          badgeCount={itemCount}
          label="Cart"
        />
      )}
      {/* Profile Button */}
      {isProfileActive ? (
        <ActiveButton
          icon={UserIcon}
          label="Profile"
          onClick={handleProfileClick}
        />
      ) : (
        <InactiveButton icon={UserIcon} onClick={handleProfileClick} label="Profile" />
      )}
    </div>
  );
};
